import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { USER_API } from "config";
import { useAxios } from "../useAxios";
import { projectKeys } from "../../queries/project.keys";

interface ProjectSummaryPayload {
  project_id: string;
  description: string | null;
  versions_count: number;
  features: {
    has_script_breakdown: boolean;
    has_character_breakdown: boolean;
    has_schedule: boolean;
    has_budget: boolean;
    has_coverage: boolean;
  } | null;
  members: Array<{
    user_id: string;
    first_name: string | null;
    last_name: string | null;
    role: string;
  }>;
}

export interface ProjectSummary {
  projectId: string;
  description: string | null;
  versionsCount: number;
  features: {
    hasScriptBreakdown: boolean;
    hasCharacterBreakdown: boolean;
    hasSchedule: boolean;
    hasBudget: boolean;
    hasCoverage: boolean;
  } | null;
  members: Array<{
    userId: string;
    firstName: string | null;
    lastName: string | null;
    role: string;
  }>;
}

const transformSummary = (payload: ProjectSummaryPayload): ProjectSummary => ({
  projectId: payload.project_id,
  description: payload.description,
  versionsCount: payload.versions_count,
  features: payload.features
    ? {
        hasScriptBreakdown: payload.features.has_script_breakdown,
        hasCharacterBreakdown: payload.features.has_character_breakdown,
        hasSchedule: payload.features.has_schedule,
        hasBudget: payload.features.has_budget,
        hasCoverage: payload.features.has_coverage,
      }
    : null,
  members: (payload.members ?? []).map((member) => ({
    userId: member.user_id,
    firstName: member.first_name,
    lastName: member.last_name,
    role: member.role,
  })),
});

/**
 * Lean per-workspace projects summary (features, members, descriptions,
 * version counts) for list views. Complements the slim user-catalog data
 * without fattening that hot payload.
 */
export const useProjectsSummary = (workspaceId?: string) => {
  const axios = useAxios({ allResponse: false });

  const query = useQuery<ProjectSummary[]>({
    queryKey: projectKeys.summary(workspaceId ?? ""),
    enabled: Boolean(workspaceId),
    staleTime: 30_000,
    queryFn: async () => {
      const response = await axios.get(USER_API.projectsSummary(workspaceId as string));
      const rows: ProjectSummaryPayload[] = Array.isArray(response)
        ? response
        : (response?.data ?? []);
      return rows.map(transformSummary);
    },
  });

  const summaryByProjectId = useMemo(
    () => new Map((query.data ?? []).map((summary) => [summary.projectId, summary])),
    [query.data]
  );

  return {
    summaries: query.data ?? [],
    summaryByProjectId,
    isLoading: query.isLoading,
    isError: query.isError,
    /** The summary failed and nothing is cached, so cards lack team and draft details. */
    loadFailed: query.isError && query.data === undefined,
    refetch: query.refetch,
  };
};
